import React from "react";
import {
  Typography,
  TextField,
  Button,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { MdOutlineFileDownload } from "react-icons/md";
import MultiSelect from "./MultiSelect";
import TimeOfDaySlider from "./TimeOfDaySlider";
import { MIN_DATE } from "../utils/constants";
import { todayStr } from "../utils/dateHelpers";

const RiskTabFilters = ({
  activeTabKey,
  t,
  canDate,
  canTime,
  canSet,
  canEmployee,
  canDownload,
  showReportingCategory,
  pdfDownloading,
  pngDownloading,
  csvDownloading,
  isRiskDownloadBusy,
  onDownloadPdf,
  onDownloadPng,
  onDownloadRiskCsv,
  onDownloadReportingCsv,
  downloadCsv,
  missionFiltered,
  guestFiltered,
  estimatedFiltered,
  estimatedData,
  dateFrom,
  setDateFrom,
  dateTo,
  setDateTo,
  timeRange,
  setTimeRange,
  setNumbers,
  setSetNumbers,
  realIncidentSetOptions,
  employees,
  setEmployees,
  employeeOptions,
  rDateFrom,
  setRDateFrom,
  rDateTo,
  setRDateTo,
  rTimeRange,
  setRTimeRange,
  rSetNumbers,
  setRSetNumbers,
  reportingCategorySetOptions,
  rEmployees,
  setREmployees,
  mDateFrom,
  setMDateFrom,
  mDateTo,
  setMDateTo,
  mTimeRange,
  setMTimeRange,
  mSetNumbers,
  setMSetNumbers,
  missionSetOptions,
  gDateFrom,
  setGDateFrom,
  gDateTo,
  setGDateTo,
  gTimeRange,
  setGTimeRange,
  gSetNumbers,
  setGSetNumbers,
  eDateFrom,
  setEDateFrom,
  eDateTo,
  setEDateTo,
  eTimeRange,
  setETimeRange,
  eSetNumbers,
  setESetNumbers,
  estimatedSetOptions,
  eBeach,
  setEBeach,
}) => {
  const renderDownloadButtons = (handlers) => (
    <div className="col-12 col-lg-4 d-flex flex-column gap-2 mt-1 risk-inline-downloads">
      {handlers}
    </div>
  );

  const pdfButton = (
    <Button
      variant="outlined"
      size="small"
      startIcon={
        pdfDownloading ? (
          <CircularProgress size={14} color="inherit" />
        ) : (
          <MdOutlineFileDownload />
        )
      }
      onClick={onDownloadPdf}
      disabled={isRiskDownloadBusy}
    >
      {t("downloadPdf")}
    </Button>
  );

  const pngButton = (
    <Button
      variant="outlined"
      size="small"
      startIcon={
        pngDownloading ? (
          <CircularProgress size={14} color="inherit" />
        ) : (
          <MdOutlineFileDownload />
        )
      }
      onClick={onDownloadPng}
      disabled={isRiskDownloadBusy}
    >
      {t("downloadPng")}
    </Button>
  );

  const csvButton = (onClick) => (
    <Button
      variant="outlined"
      size="small"
      startIcon={
        csvDownloading ? (
          <CircularProgress size={14} color="inherit" />
        ) : (
          <MdOutlineFileDownload />
        )
      }
      onClick={onClick}
      disabled={isRiskDownloadBusy}
    >
      {t("downloadCsv")}
    </Button>
  );

  const dateRangeFields = (from, setFrom, to, setTo) => (
    <div className="d-flex gap-2">
      <TextField
        type="date"
        size="small"
        fullWidth
        value={from}
        onChange={(e) => setFrom(e.target.value)}
        inputProps={{ min: MIN_DATE, max: todayStr() }}
      />
      <TextField
        type="date"
        size="small"
        fullWidth
        value={to}
        onChange={(e) => setTo(e.target.value)}
        inputProps={{ min: MIN_DATE, max: todayStr() }}
      />
    </div>
  );

  if (activeTabKey === "faktuel") {
    return (
      <>
        <div className="row mt-4">
          {canDate && (
            <div className="col-12 col-lg-4">
              <Typography variant="body2" fontWeight={600} gutterBottom>
                {t("timePeriod")}
              </Typography>
              {dateRangeFields(dateFrom, setDateFrom, dateTo, setDateTo)}
            </div>
          )}

          {canSet && (
            <div className="col-12 col-lg-4">
              <Typography variant="body2" fontWeight={600} gutterBottom>
                {t("setNumber")}
              </Typography>
              <MultiSelect
                label=""
                options={realIncidentSetOptions}
                value={setNumbers}
                onChange={(sel) => setSetNumbers(sel)}
                onDelete={(v) => setSetNumbers((p) => p.filter((x) => x !== v))}
              />
            </div>
          )}

          {renderDownloadButtons(
            <>
              {pdfButton}
              {pngButton}
              {canDownload && csvButton(onDownloadRiskCsv)}
            </>,
          )}
        </div>

        <div className="row mt-4">
          {canTime && (
            <div className="col-12 col-lg-4">
              <TimeOfDaySlider
                label={t("timeOfDay")}
                timeRange={timeRange}
                onChange={setTimeRange}
              />
            </div>
          )}
          {canEmployee && (
            <div className="col-12 col-lg-4">
              <Typography variant="body2" fontWeight={600} gutterBottom>
                {t("employee")}
              </Typography>
              <MultiSelect
                label=""
                options={employeeOptions}
                value={employees}
                onChange={(sel) => setEmployees(sel)}
                onDelete={(v) => setEmployees((p) => p.filter((x) => x !== v))}
              />
            </div>
          )}

          {showReportingCategory ? (
            <div className="col-12 col-lg-4 risk-chart-side-filter-spacer" />
          ) : null}
        </div>
      </>
    );
  }

  if (activeTabKey === "reportingCategory") {
    return (
      <>
        <div className="row mt-4">
          {canDate && (
            <div className="col-12 col-lg-4">
              <Typography variant="body2" fontWeight={600} gutterBottom>
                {t("timePeriod")}
              </Typography>
              {dateRangeFields(rDateFrom, setRDateFrom, rDateTo, setRDateTo)}
            </div>
          )}

          {canSet && (
            <div className="col-12 col-lg-4">
              <Typography variant="body2" fontWeight={600} gutterBottom>
                {t("setNumber")}
              </Typography>
              <MultiSelect
                label=""
                options={reportingCategorySetOptions}
                value={rSetNumbers}
                onChange={(sel) => setRSetNumbers(sel)}
                onDelete={(value) =>
                  setRSetNumbers((current) =>
                    current.filter((item) => item !== value),
                  )
                }
              />
            </div>
          )}

          {canDownload &&
            renderDownloadButtons(csvButton(onDownloadReportingCsv))}
        </div>

        <div className="row mt-4">
          {canTime && (
            <div className="col-12 col-lg-4">
              <TimeOfDaySlider
                label={t("timeOfDay")}
                timeRange={rTimeRange}
                onChange={setRTimeRange}
              />
            </div>
          )}

          {canEmployee && (
            <div className="col-12 col-lg-4">
              <Typography variant="body2" fontWeight={600} gutterBottom>
                {t("employee")}
              </Typography>
              <MultiSelect
                label=""
                options={employeeOptions}
                value={rEmployees}
                onChange={(sel) => setREmployees(sel)}
                onDelete={(value) =>
                  setREmployees((current) =>
                    current.filter((item) => item !== value),
                  )
                }
              />
            </div>
          )}

          <div className="col-12 col-lg-4 risk-chart-side-filter-spacer" />
        </div>
      </>
    );
  }

  if (activeTabKey === "performance") {
    return (
      <>
        <div className="row mt-4">
          {canDate && (
            <div className="col-12 col-lg-4">
              <Typography variant="body2" fontWeight={600} gutterBottom>
                {t("timePeriod")}
              </Typography>
              {dateRangeFields(dateFrom, setDateFrom, dateTo, setDateTo)}
            </div>
          )}

          {renderDownloadButtons(
            <>
              {pdfButton}
              {pngButton}
              {canDownload && csvButton(onDownloadRiskCsv)}
            </>,
          )}
        </div>

        <div className="row mt-4">
          {canTime && (
            <div className="col-12 col-lg-4">
              <TimeOfDaySlider
                label={t("timeOfDay")}
                timeRange={timeRange}
                onChange={setTimeRange}
                variant="compact"
              />
            </div>
          )}
        </div>
      </>
    );
  }

  if (activeTabKey === "mission") {
    return (
      <>
        <div className="row g-3 mt-3">
          {canDate && (
            <div className="col-12 col-lg-4">
              <Typography variant="body2" fontWeight={700} gutterBottom>
                {t("timePeriod")}
              </Typography>
              {dateRangeFields(mDateFrom, setMDateFrom, mDateTo, setMDateTo)}
            </div>
          )}

          {canSet && (
            <div className="col-12 col-lg-4">
              <Typography variant="body2" fontWeight={700} gutterBottom>
                {t("setNumber")}
              </Typography>
              <MultiSelect
                label=""
                options={missionSetOptions}
                value={mSetNumbers}
                onChange={(sel) => setMSetNumbers(sel)}
                onDelete={(v) => setMSetNumbers((p) => p.filter((x) => x !== v))}
              />
            </div>
          )}

          {canDownload &&
            renderDownloadButtons(
              csvButton(() => downloadCsv(missionFiltered, "mission.csv")),
            )}
        </div>

        <div className="row g-3 mt-2">
          {canTime && (
            <div className="col-12 col-lg-4">
              <TimeOfDaySlider
                label={t("timeOfDay")}
                timeRange={mTimeRange}
                onChange={setMTimeRange}
              />
            </div>
          )}
        </div>
      </>
    );
  }

  if (activeTabKey === "guest") {
    return (
      <>
        <div className="row g-3 mt-3">
          {canDate && (
            <div className="col-12 col-lg-4">
              <Typography variant="body2" fontWeight={700} gutterBottom>
                {t("timePeriod")}
              </Typography>
              {dateRangeFields(gDateFrom, setGDateFrom, gDateTo, setGDateTo)}
            </div>
          )}

          {canSet && (
            <div className="col-12 col-lg-4">
              <Typography variant="body2" fontWeight={700} gutterBottom>
                {t("setNumber")}
              </Typography>
              <MultiSelect
                label=""
                options={missionSetOptions}
                value={gSetNumbers}
                onChange={(sel) => setGSetNumbers(sel)}
                onDelete={(v) => setGSetNumbers((p) => p.filter((x) => x !== v))}
              />
            </div>
          )}

          {canDownload &&
            renderDownloadButtons(
              csvButton(() => downloadCsv(guestFiltered, "guest.csv")),
            )}
        </div>

        <div className="row g-3 mt-2">
          {canTime && (
            <div className="col-12 col-lg-4">
              <TimeOfDaySlider
                label={t("timeOfDay")}
                timeRange={gTimeRange}
                onChange={setGTimeRange}
              />
            </div>
          )}
        </div>
      </>
    );
  }

  if (activeTabKey === "estimated") {
    return (
      <>
        <div className="row g-3 mt-3">
          {canDate && (
            <div className="col-12 col-lg-4">
              <Typography variant="body2" fontWeight={700} gutterBottom>
                {t("timePeriod")}
              </Typography>
              {dateRangeFields(eDateFrom, setEDateFrom, eDateTo, setEDateTo)}
            </div>
          )}

          {canSet && (
            <div className="col-12 col-lg-4">
              <Typography variant="body2" fontWeight={700} gutterBottom>
                {t("setNumber")}
              </Typography>
              <MultiSelect
                label=""
                options={estimatedSetOptions}
                value={eSetNumbers}
                onChange={(sel) => setESetNumbers(sel)}
                onDelete={(v) => setESetNumbers((p) => p.filter((x) => x !== v))}
              />
            </div>
          )}

          {canDownload &&
            renderDownloadButtons(
              csvButton(() => downloadCsv(estimatedFiltered, "estimated.csv")),
            )}
        </div>

        <div className="row g-3 mt-2">
          {canTime && (
            <div className="col-12 col-lg-4">
              <TimeOfDaySlider
                label={t("timeOfDay")}
                timeRange={eTimeRange}
                onChange={setETimeRange}
              />
            </div>
          )}
          <div className="col-12 col-lg-4">
            <Typography variant="body2" fontWeight={700} gutterBottom>
              {t("beaches")}
            </Typography>
            <TextField
              select
              size="small"
              fullWidth
              value={eBeach}
              onChange={(e) => setEBeach(e.target.value)}
              inputProps={{ style: { fontSize: 13 } }}
            >
              <MenuItem value="">
                <em>Alle strande</em>
              </MenuItem>
              {Array.from(
                new Set(
                  (estimatedData ?? []).map((r) => r.beach).filter(Boolean),
                ),
              )
                .sort()
                .map((b) => (
                  <MenuItem key={b} value={b}>
                    {b}
                  </MenuItem>
                ))}
            </TextField>
          </div>
        </div>
      </>
    );
  }

  return null;
};

export default RiskTabFilters;
